import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Layers, MessageSquare, Package } from "lucide-react";
import BlockRenderer from "../components/BlockRenderer";
import SmartImage from "../components/SmartImage";
import { enquireHref, pageHref } from "../lib/format";
import {
  CrumbLine,
  CtaPair,
  EmptyCta,
  HeroBand,
  SectionHead,
  SiblingPills,
  StatChip,
  useEnquiry,
} from "./parts";

function MetaRow({ child }) {
  if (!child.child_count && !child.item_count) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-[color:var(--nq-stone)]">
      {child.child_count > 0 && (
        <span className="flex items-center gap-1.5">
          <Layers className="h-3.5 w-3.5 gold-text" /> {child.child_count} sub-categories
        </span>
      )}
      {child.item_count > 0 && (
        <span className="flex items-center gap-1.5">
          <Package className="h-3.5 w-3.5 gold-text" /> {child.item_count} products
        </span>
      )}
    </div>
  );
}

/** Large editorial card for the first child of a hub. */
function FeatureCard({ child, parentLabel }) {
  return (
    <article className="card-nq group relative overflow-hidden" data-testid="hub-feature-card">
      <Link to={pageHref(child.slug)} className="block">
        <div className="relative">
          <SmartImage
            src={child.image}
            alt={child.label}
            ratio="aspect-[16/9] md:aspect-[21/9]"
            fit="cover"
            w={1600}
            fallbackLabel={child.label}
            className="transition-transform duration-500 group-hover:scale-[1.03]"
            loading="eager"
          />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(to top, rgba(11,13,12,0.94) 12%, rgba(11,13,12,0.15))" }}
          />
        </div>
      </Link>

      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-8">
        <p className="eyebrow">Start here</p>
        <h3 className="mt-2 text-xl font-bold text-[color:var(--nq-sand)] md:text-3xl">
          <Link
            to={pageHref(child.slug)}
            className="transition-colors duration-200 hover:text-[color:var(--nq-gold)]"
          >
            {child.label}
          </Link>
        </h3>
        {child.excerpt && (
          <p className="mt-2 line-clamp-2 max-w-2xl text-sm text-[color:var(--nq-stone)]">
            {child.excerpt}
          </p>
        )}
        <MetaRow child={child} />
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to={pageHref(child.slug)}
            className="btn-gold px-4 py-2 text-xs"
            data-testid="hub-feature-view-cta"
          >
            Explore {child.label} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Link
            to={enquireHref({
              type: "page",
              id: child.slug,
              title: child.label,
              image: child.image,
              category: parentLabel,
            })}
            className="btn-outline-nq px-4 py-2 text-xs"
            data-testid="hub-feature-enquire-cta"
          >
            <MessageSquare className="h-3.5 w-3.5" /> Enquire
          </Link>
        </div>
      </div>
    </article>
  );
}

/** Compact horizontal card - image left, copy right. */
function RowCard({ child, index, parentLabel }) {
  return (
    <article className="card-nq group flex overflow-hidden" data-testid="hub-category-card">
      <Link to={pageHref(child.slug)} className="relative w-1/3 shrink-0 overflow-hidden sm:w-44">
        <SmartImage
          src={child.image}
          alt={child.label}
          fill
          fit="cover"
          w={480}
          fallbackLabel={child.label}
          className="transition-transform duration-500 group-hover:scale-[1.06]"
        />
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-base font-semibold text-[color:var(--nq-sand)] md:text-lg">
            <Link
              to={pageHref(child.slug)}
              className="transition-colors duration-200 hover:text-[color:var(--nq-gold)]"
            >
              {child.label}
            </Link>
          </h3>
          <span className="font-mono text-xs opacity-40">
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>
        {child.excerpt && (
          <p className="mt-2 line-clamp-2 text-sm text-[color:var(--nq-stone)]">{child.excerpt}</p>
        )}
        <MetaRow child={child} />
        <div className="mt-auto flex gap-2 pt-4">
          <Link
            to={pageHref(child.slug)}
            className="btn-outline-nq flex-1 px-3 py-2 text-xs"
            data-testid="hub-card-view-cta"
          >
            View range <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Link
            to={enquireHref({
              type: "page",
              id: child.slug,
              title: child.label,
              image: child.image,
              category: parentLabel,
            })}
            className="btn-gold px-3 py-2 text-xs"
            aria-label={`Enquire about ${child.label}`}
            data-testid="hub-card-enquire-cta"
          >
            <MessageSquare className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </article>
  );
}

/**
 * HUB page - the source site's menu-only category landings had completely
 * empty bodies. This builds a real directory: one large feature card plus a
 * two-column row-card index, so the page has structure instead of a blank.
 */
export default function HubTemplate({
  page,
  childPages: kids = [],
  siblings = [],
  breadcrumbs = [],
  parentLabel,
  phone,
}) {
  const heroImg = page.hero_image || page.thumb_image;
  const to = useEnquiry(page, heroImg, parentLabel);
  const heading = page.h1 && page.h1.length < 90 ? page.h1 : page.title;
  const blocks = page.blocks || [];
  const [feature, ...rest] = kids;
  const totalItems = kids.reduce((n, k) => n + (k.item_count || 0), 0);

  return (
    <div data-testid="template-hub">
      <HeroBand image={heroImg} dim={0.16}>
        <CrumbLine breadcrumbs={breadcrumbs} parentLabel={parentLabel} />
        <div className="mt-2 grid gap-6 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8">
            <h1
              className="text-3xl font-bold leading-tight text-[color:var(--nq-sand)] md:text-5xl"
              data-testid="page-title"
            >
              {heading}
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[color:var(--nq-stone)] md:text-base">
              {page.meta_description ||
                `Browse the ${page.title.toLowerCase()} we supply and fit. Pick a category below, or send us your vehicle details and we'll recommend the right setup.`}
            </p>
            <div className="mt-7">
              <CtaPair to={to} label={`Enquire about ${page.title.slice(0, 24)}`} phone={phone} />
            </div>
          </div>
          <div className="flex flex-wrap gap-3 lg:col-span-4 lg:justify-end">
            {kids.length > 0 && <StatChip value={kids.length} label="Categories" />}
            {totalItems > 0 && <StatChip value={totalItems} label="Products" />}
          </div>
        </div>
      </HeroBand>

      <div className="container-nq section-nq space-y-14">
        {kids.length > 0 ? (
          <section>
            <SectionHead
              eyebrow="Categories"
              title={`Everything under ${page.title}`}
              sub="Each category lists the brands and parts we stock, with enquiry-based pricing and fitment advice."
            />
            <div className="space-y-5" data-testid="hub-category-grid">
              {feature && <FeatureCard child={feature} parentLabel={page.title} />}
              {rest.length > 0 && (
                <div className="grid gap-5 md:grid-cols-2">
                  {rest.map((c, i) => (
                    <RowCard key={c.slug} child={c} index={i + 1} parentLabel={page.title} />
                  ))}
                </div>
              )}
            </div>
          </section>
        ) : (
          <EmptyCta page={page} to={to} siblings={siblings} />
        )}

        {blocks.length > 0 && (
          <section className="max-w-3xl">
            <BlockRenderer blocks={blocks} />
          </section>
        )}

        {kids.length > 0 && <SiblingPills siblings={siblings} />}
      </div>
    </div>
  );
}
